import { useGetAtRiskStudents } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Input from '@components/V4/Inputs/Input';
import Select from '@components/V4/Select/Select';
import { ExclamationIcon, ShieldExclamationIcon, UserGroupIcon } from '@heroicons/react/outline';
import {
  EmptyState,
  FilterField,
  MetricCard,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import DetailsModal from '@modules/Admin/components/DetailsModal/DetailsModal';
import useDetailsModal from '@modules/Admin/hooks/useDetailsModal';
import { AtRiskStudentDTO, RiskLevel, RiskSignalType } from '@shared/apis/studentInsights.apis';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { stringify } from 'csv-stringify/sync';
import moment from 'moment';
import { useMemo, useState } from 'react';

const PAGE_SIZE = 50;

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow =
  'cursor-pointer border-b border-white/5 align-top transition-colors last:border-b-0 hover:bg-surfaceHover';
const leftHead = 'px-5 py-3 text-left font-semibold';
const leftCell = 'px-5 py-3 text-left text-customLight';

const LEVEL_STYLES: Record<RiskLevel, string> = {
  high: 'bg-red-500/15 text-red-300',
  medium: 'bg-amber-500/15 text-amber-300',
  low: 'bg-white/10 text-customGrey',
};

const LEVEL_OPTIONS = [
  { label: 'High and medium', value: 'medium' },
  { label: 'High only', value: 'high' },
  { label: 'Everyone with a signal', value: 'low' },
];

const LEVEL_RANK: Record<RiskLevel, number> = { high: 2, medium: 1, low: 0 };

const SIGNAL_OPTIONS: { label: string; value: RiskSignalType }[] = [
  { label: 'No meeting held yet', value: 'never_met' },
  { label: 'No meeting in 30+ days', value: 'inactive' },
  { label: 'Nothing scheduled', value: 'no_upcoming_meeting' },
  { label: 'Missed or cancelled meetings', value: 'missed_meetings' },
  { label: 'Low mentor ratings', value: 'low_mentor_ratings' },
  { label: 'Unhappy parent', value: 'low_parent_feedback' },
  { label: 'Unhappy student', value: 'low_student_feedback' },
  { label: 'Low NPS', value: 'low_nps' },
  { label: 'High churn score', value: 'high_churn_score' },
  { label: 'Payment overdue', value: 'payment_overdue' },
  { label: 'Emails not reaching them', value: 'emails_failing' },
  { label: 'Open flags', value: 'open_flags' },
];

const formatDay = (value?: string) => (value ? moment(value).format('DD MMM YYYY') : '—');

const exportCsv = (students: AtRiskStudentDTO[]) => {
  const csv = stringify([
    [
      'Student',
      'Email',
      'Graduation year',
      'Risk',
      'Score',
      'Signals',
      'Courses',
      'Mentors',
      'Last meeting',
      'Next meeting',
    ],
    ...students.map((student) => [
      student.name,
      student.email,
      student.graduationYear ?? '',
      student.level,
      student.score,
      student.signals.map((signal) => `${signal.label}: ${signal.detail}`).join('; '),
      student.courses.join('; '),
      student.mentors.join('; '),
      formatDay(student.lastMeetingAt),
      formatDay(student.nextMeetingAt),
    ]),
  ]);
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `at-risk-students-${moment().format('YYYY-MM-DD')}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const AtRiskStudents = () => {
  const { data, isLoading } = useGetAtRiskStudents();
  const [minimumLevel, setMinimumLevel] = useState<RiskLevel>('medium');
  const [signalTypes, setSignalTypes] = useState<RiskSignalType[]>([]);
  const [mentors, setMentors] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const {
    activeModalView,
    isModalOpen,
    mentorId,
    studentId,
    sessionId,
    enrollmentId,
    openModal,
    closeModal,
  } = useDetailsModal({ page: 'student', tab: 'timeline' });

  const students = useMemo(() => data?.students ?? [], [data]);

  const mentorOptions = useMemo(
    () =>
      [...new Set(students.flatMap((student) => student.mentors))]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ label: name, value: name })),
    [students]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return students.filter(
      (student) =>
        LEVEL_RANK[student.level] >= LEVEL_RANK[minimumLevel] &&
        (!signalTypes.length ||
          signalTypes.every((type) => student.signals.some((signal) => signal.type === type))) &&
        (!mentors.length || mentors.some((mentor) => student.mentors.includes(mentor))) &&
        (!term ||
          student.name.toLowerCase().includes(term) ||
          student.email.toLowerCase().includes(term))
    );
  }, [students, minimumLevel, signalTypes, mentors, search]);

  const counts = useMemo(
    () => ({
      high: students.filter((student) => student.level === 'high').length,
      medium: students.filter((student) => student.level === 'medium').length,
    }),
    [students]
  );

  if (isLoading || !data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const resetPaging = () => setVisibleCount(PAGE_SIZE);

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold">At-risk Students</h1>
          <p className="max-w-3xl text-sm text-customGrey">
            Students with an active enrollment, scored from meetings, feedback, NPS, churn score,
            payments, undelivered emails and open flags. Students enrolled in the last 14 days are
            not judged on meeting activity yet. Click a student to open their timeline.
          </p>
        </div>
        <Button
          variant="secondary"
          buttonText="Export CSV"
          disabled={!filtered.length}
          onClick={() => exportCsv(filtered)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-3">
        <MetricCard
          label="High risk"
          value={counts.high}
          icon={<ShieldExclamationIcon className="h-5 w-5" />}
          accent="lila"
          onClick={() => {
            setMinimumLevel('high');
            resetPaging();
          }}
        />
        <MetricCard
          label="Medium risk"
          value={counts.medium}
          icon={<ExclamationIcon className="h-5 w-5" />}
          accent="gold"
          onClick={() => {
            setMinimumLevel('medium');
            resetPaging();
          }}
        />
        <MetricCard
          label="Active students checked"
          value={data.activeStudents}
          icon={<UserGroupIcon className="h-5 w-5" />}
          accent="teal"
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterField width="tablet:w-64">
          <Input
            type="text"
            name="at-risk-search"
            id="at-risk-search"
            label="Search"
            placeholder="Name or email"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              resetPaging();
            }}
          />
        </FilterField>
        <FilterField width="tablet:w-56">
          <Select
            variant="secondary"
            label="Risk"
            allowClear={false}
            value={minimumLevel}
            options={LEVEL_OPTIONS}
            onChange={(value) => {
              setMinimumLevel(value as RiskLevel);
              resetPaging();
            }}
          />
        </FilterField>
        <FilterField width="tablet:w-72">
          <Select
            variant="secondary"
            label="Has all of these signals"
            mode="multiple"
            placeholder="Any signal"
            value={signalTypes}
            options={SIGNAL_OPTIONS}
            onChange={(value) => {
              setSignalTypes((value as RiskSignalType[]) ?? []);
              resetPaging();
            }}
          />
        </FilterField>
        <FilterField width="tablet:w-64">
          <Select
            variant="secondary"
            label="Mentor"
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder="Any mentor"
            value={mentors}
            options={mentorOptions}
            onChange={(value) => {
              setMentors((value as string[]) ?? []);
              resetPaging();
            }}
          />
        </FilterField>
      </div>

      <section className="flex flex-col gap-4">
        <SectionHeading
          title={`Students (${filtered.length})`}
          description={`Calculated ${moment(
            data.generatedAt
          ).fromNow()}. Score = sum of signal weights; 6+ is high, 3–5 medium.`}
        />
        {!filtered.length ? (
          <EmptyState>No students match these filters.</EmptyState>
        ) : (
          <>
            <TableShell minWidth="min-w-[1100px]">
              <thead>
                <tr className={headRow}>
                  <th className={leftHead}>Student</th>
                  <th className={leftHead}>Risk</th>
                  <th className={leftHead}>Signals</th>
                  <th className={leftHead}>Mentors</th>
                  <th className={leftHead}>Last / next meeting</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, visibleCount).map((student) => (
                  <tr
                    key={student.studentId}
                    className={bodyRow}
                    tabIndex={0}
                    onClick={() =>
                      openModal(
                        { page: 'student', tab: 'timeline' },
                        { studentId: student.studentId }
                      )
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        openModal(
                          { page: 'student', tab: 'timeline' },
                          { studentId: student.studentId }
                        );
                      }
                    }}
                  >
                    <td className={leftCell}>
                      <div className="font-semibold text-white">{student.name}</div>
                      <div className="text-xs text-customGrey">
                        {student.email}
                        {student.graduationYear ? ` · class of ${student.graduationYear}` : ''}
                      </div>
                      <div className="mt-1 text-xs text-customGrey">
                        {student.courses.join(', ')}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={mergeClassNames(
                          'whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold capitalize',
                          LEVEL_STYLES[student.level]
                        )}
                      >
                        {student.level} · {student.score}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <ul className="flex flex-col gap-1">
                        {student.signals.map((signal) => (
                          <li key={signal.type} className="text-xs">
                            <span className="font-semibold text-white">{signal.label}</span>
                            <span className="text-customGrey"> · {signal.detail}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className={mergeClassNames(leftCell, 'text-xs')}>
                      {student.mentors.join(', ') || '—'}
                    </td>
                    <td className={mergeClassNames(leftCell, 'whitespace-nowrap text-xs')}>
                      <div>Last: {formatDay(student.lastMeetingAt)}</div>
                      <div className="text-customGrey">
                        Next: {formatDay(student.nextMeetingAt)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
            {filtered.length > visibleCount ? (
              <button
                type="button"
                className="self-start text-sm text-customPrimary"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              >
                Show more ({filtered.length - visibleCount} left)
              </button>
            ) : null}
          </>
        )}
      </section>

      {isModalOpen && (
        <DetailsModal
          {...activeModalView}
          mentorId={mentorId}
          studentId={studentId}
          enrollmentId={enrollmentId}
          sessionId={sessionId}
          isOpen={true}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default AtRiskStudents;
